import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Button, Form, Table, Spinner, Nav, Row, Col, Alert } from 'react-bootstrap'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { TableSkeleton } from '../../../../shared/components/LoadingSkeletons'
import { useAuth } from '../../../auth'
import { QHSE_PERMS } from '../../core/permissions'
import { useQhseRouteAccess } from '../../core/menuAccess'
import type {
  RiskLibraryCategory,
  RiskLibraryTaskSummary,
  RiskLibraryTaskDetail,
  RiskLibraryCompanyHeader,
  RiskLibraryTaskWritePayload,
  RiskLibraryHazardWritePayload,
  RiskLibraryTaskFailureWritePayload,
  RiskLibraryVesselOption,
} from '../../core/_models'
import {
  listRiskLibraryCategories,
  listRiskLibraryTasks,
  listRiskLibraryCategoryTaskDetails,
  getRiskLibraryTask,
  createRiskLibraryTask,
  updateRiskLibraryTask,
  deleteRiskLibraryTask,
  getRiskLibraryCompanyHeader,
  saveRiskLibraryCompanyHeader,
  listRiskLibraryVesselOptions,
  promoteRiskLibraryTaskToCompanyScope,
} from '../../core/_requests'
import {
  formatRiskLibraryDateOnly,
  formatRiskLibraryTaskVersion,
  likelihoodBadge,
  niceEnum,
  riskFactorBadge,
  sectionCardStyle,
  severityBadge,
} from './riskLibraryTaskDetailDisplay'
import { RiskLibraryTaskDetailReadOnlyView } from './RiskLibraryTaskDetailReadOnlyView'
import {
  exportRiskLibraryCategoryWorksheetPdf,
  exportRiskLibraryMasterListPdf,
} from './riskAssessmentLibraryPdfExport'
import { isLikelyVesselAssignedUser } from '../../core/qhseUserContext'
import { useGlobalFilter } from '../../../../shared/context/GlobalFilterContext'

/** SAF-015 style column header colours */
const CATEGORY_HEADER_BG: Record<string, string> = {
  DECK_ROUTINE: '#2f5f9e',
  DECK_NON_ROUTINE: '#6b5b95',
  ENGINE_ROUTINE: '#2d7a4a',
  ENGINE_NON_ROUTINE: '#5a9a6e',
  CRITICAL_EQUIPMENT: '#c78b2b',
}

function categoryHeaderColor(code: string) {
  return CATEGORY_HEADER_BG[code] || '#5c6b7a'
}

const FORM_LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const LIKELIHOOD_OPTIONS = [
  'ALMOST_CERTAIN',
  'LIKELY',
  'POSSIBLE',
  'UNLIKELY',
  'REMOTE',
] as const
const SEVERITY_OPTIONS = ['NEGLIGIBLE', 'MINOR', 'SIGNIFICANT', 'CRITICAL'] as const
const RISK_FACTOR_OPTIONS = ['LOW', 'MEDIUM', 'HIGH'] as const

const stickyModalFooterStyle: React.CSSProperties = {
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  background: '#fff',
  borderTop: '1px solid #e9ecef',
}

/** Scroll long worksheet / header forms inside the modal while keeping title + actions visible */
const libraryModalBodyScrollStyle: React.CSSProperties = {
  maxHeight: 'min(calc(100vh - 12rem), 52rem)',
  overflowY: 'auto',
}

function detailToWritePayload(d: RiskLibraryTaskDetail): RiskLibraryTaskWritePayload {
  if (d.categoryCode === 'CRITICAL_EQUIPMENT') {
    return {
      categoryId: d.categoryId,
      categoryTaskNumber: d.categoryTaskNumber,
      title: d.title,
      workActivity: undefined,
      equipmentName: d.title,
      failureMode: undefined,
      hazards: [],
      failures: (d.failures || []).map((f, idx) => ({
        id: f.id,
        sortOrder: f.sortOrder ?? idx,
        failureText: f.failureText,
        likelihood: f.likelihood,
        severity: f.severity,
        riskFactor: f.riskFactor,
        hazardPoints:
          (f.hazardPoints || []).length > 0
            ? f.hazardPoints.map((p) => p.pointText)
            : [''],
        existingControlLines:
          (f.existingControls || []).length > 0
            ? f.existingControls.map((c) => c.lineText)
            : [''],
        furtherControlLines:
          (f.furtherControls || []).length > 0
            ? f.furtherControls.map((c) => c.lineText)
            : [''],
      })),
    }
  }
  return {
    categoryId: d.categoryId,
    categoryTaskNumber: d.categoryTaskNumber,
    title: d.title,
    workActivity: d.workActivity ?? undefined,
    equipmentName: d.equipmentName ?? undefined,
    failureMode: d.failureMode ?? undefined,
    hazards: (d.hazards || []).map((h, idx) => ({
      id: h.id,
      sortOrder: h.sortOrder ?? idx,
      hazardText: h.hazardText,
      likelihood: h.likelihood,
      severity: h.severity,
      riskFactor: h.riskFactor,
      existingControlLines: (h.existingControls || []).map((c) => c.lineText),
      furtherControlLines: (h.furtherControls || []).map((c) => c.lineText),
    })),
    failures: [],
  }
}

type HazardFormRow = RiskLibraryHazardWritePayload
type FailureFormRow = RiskLibraryTaskFailureWritePayload

const emptyHazard = (): HazardFormRow => ({
  hazardText: '',
  likelihood: 'POSSIBLE',
  severity: 'SIGNIFICANT',
  riskFactor: 'MEDIUM',
  existingControlLines: [''],
  furtherControlLines: [''],
})

const emptyFailure = (): FailureFormRow => ({
  failureText: '',
  likelihood: 'POSSIBLE',
  severity: 'SIGNIFICANT',
  riskFactor: 'MEDIUM',
  hazardPoints: [''],
  existingControlLines: [''],
  furtherControlLines: [''],
})

function validateHazardRowsForSave(hazards: HazardFormRow[]): string | null {
  if (!hazards.length) return 'Add at least one hazard, or cancel.'
  for (let i = 0; i < hazards.length; i++) {
    if (!hazards[i].hazardText.trim()) {
      return `Hazard ${i + 1}: Provide a hazard name or remove this hazard if it is not needed.`
    }
  }
  return null
}

function validateFailureRowsForSave(failures: FailureFormRow[]): string | null {
  if (!failures.length) return 'Add at least one failure, or cancel.'
  for (let i = 0; i < failures.length; i++) {
    const f = failures[i]
    if (!f.failureText.trim()) {
      return `Failure ${i + 1}: Provide a failure description or remove this failure if it is not needed.`
    }
    const hazardPts = f.hazardPoints.map((x) => x.trim()).filter(Boolean)
    if (hazardPts.length === 0) {
      return `Failure ${i + 1}: Add at least one hazard point or remove this failure if it is not needed.`
    }
  }
  return null
}

type MainTab = 'master' | 'category'

const RiskAssessmentLibraryPage: FC = () => {
  const { can, currentUser } = useAuth()
  const { canAdd, canEdit, canDelete } = useQhseRouteAccess('riskAssessmentLibrary')

  const hasCompanyContext = Boolean(
    currentUser?.companyGroupAdminId ??
      currentUser?.companyGroupAdmin?.id ??
      (currentUser as any)?.companyGroupAdminId
  )

  const permCreate = can(QHSE_PERMS.RISK_LIB_TASK_CREATE)
  const permUpdate = canEdit
  const permDelete = can(QHSE_PERMS.RISK_LIB_TASK_DELETE)
  const permGlobal = can(QHSE_PERMS.RISK_LIB_GLOBAL_MANAGE)
  const permDownload = can(QHSE_PERMS.RISK_DOWNLOAD)
  /** Matches backend: superadmin or any role with MANAGE_QHSE_RISK_LIBRARY_GLOBAL (e.g. operator). */
  const canManageGlobalLibraryUi = permGlobal

  const { state: globalState, companies, setCompany } = useGlobalFilter()
  const companyOptions = useMemo(() => {
    const options = companies.filter((company) => company.type === 'company_group')
    const selected = globalState.company
    if (selected?.type === 'company_group' && !options.some((company) => company.id === selected.id)) {
      return [...options, selected]
    }
    return options
  }, [companies, globalState.company])

  const [mainTab, setMainTab] = useState<MainTab>('master')
  const superadminCompanyId = globalState.company?.id ? Number(globalState.company.id) : null

  const forCompanyGroupApi = useMemo(
    () => (canManageGlobalLibraryUi && superadminCompanyId != null ? superadminCompanyId : undefined),
    [canManageGlobalLibraryUi, superadminCompanyId]
  )

  const shipboardUser = isLikelyVesselAssignedUser(currentUser)

  const [libraryVesselOptions, setLibraryVesselOptions] = useState<RiskLibraryVesselOption[]>([])
  const [formScopeVesselId, setFormScopeVesselId] = useState<number | ''>('')

  const forVesselApi = useMemo(() => {
    if (shipboardUser) return undefined
    return globalState.vessel?.id ? Number(globalState.vessel.id) : undefined
  }, [shipboardUser, globalState.vessel?.id])

  const canLoadCompanyHeader = hasCompanyContext || (canManageGlobalLibraryUi && superadminCompanyId != null)
  const canEditCompanyHeader = Boolean(permUpdate) && canLoadCompanyHeader

  const headerEditingCompanyLabel = useMemo(() => {
    if (canManageGlobalLibraryUi && superadminCompanyId != null) {
      return globalState.company?.name ?? `Company group #${superadminCompanyId}`
    }
    if (hasCompanyContext) {
      const n = currentUser?.companyGroupAdmin?.name
      return n?.trim() ? n.trim() : 'Your company group'
    }
    return '—'
  }, [canManageGlobalLibraryUi, superadminCompanyId, globalState.company?.name, hasCompanyContext, currentUser])

  const [categories, setCategories] = useState<RiskLibraryCategory[]>([])
  const [catLoading, setCatLoading] = useState(true)
  const [selectedCatId, setSelectedCatId] = useState<number | null>(null)

  const [matrixByCat, setMatrixByCat] = useState<Record<number, RiskLibraryTaskSummary[]>>({})
  const [matrixLoading, setMatrixLoading] = useState(false)

  const [tasks, setTasks] = useState<RiskLibraryTaskSummary[]>([])
  const [tasksLoading, setTasksLoading] = useState(false)
  const [search, setSearch] = useState('')

  const [libraryHeader, setLibraryHeader] = useState<RiskLibraryCompanyHeader>({})

  const [sheetTasks, setSheetTasks] = useState<RiskLibraryTaskDetail[]>([])
  const [sheetLoading, setSheetLoading] = useState(false)

  const [detailId, setDetailId] = useState<number | null>(null)
  const [detail, setDetail] = useState<RiskLibraryTaskDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailModalEdit, setDetailModalEdit] = useState(false)
  const [dmTitle, setDmTitle] = useState('')
  const [dmWorkActivity, setDmWorkActivity] = useState('')
  const [dmHazards, setDmHazards] = useState<HazardFormRow[]>([emptyHazard()])
  const [dmFailures, setDmFailures] = useState<FailureFormRow[]>([emptyFailure()])
  const [dmSaving, setDmSaving] = useState(false)

  const [editOpen, setEditOpen] = useState(false)
  const [editGlobal, setEditGlobal] = useState(false)
  const [formTitle, setFormTitle] = useState('')
  const [formWorkActivity, setFormWorkActivity] = useState('')
  const [formHazards, setFormHazards] = useState<HazardFormRow[]>([emptyHazard()])
  const [formFailures, setFormFailures] = useState<FailureFormRow[]>([emptyFailure()])
  const [saving, setSaving] = useState(false)

  const [headerOpen, setHeaderOpen] = useState(false)
  const [header, setHeader] = useState<RiskLibraryCompanyHeader>({})
  const [headerLoading, setHeaderLoading] = useState(false)

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCatId) || null,
    [categories, selectedCatId]
  )

  const isCriticalCategory = selectedCategory?.code === 'CRITICAL_EQUIPMENT'

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setCatLoading(true)
        const c = await listRiskLibraryCategories()
        if (cancelled) return
        setCategories(c)
        if (c.length && selectedCatId == null) {
          setSelectedCatId(c[0].id)
        }
      } catch (e: any) {
        toast.error('Failed to load categories')
      } finally {
        if (!cancelled) setCatLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (globalState.searchQuery !== undefined && globalState.searchQuery !== '') {
      setSearch(globalState.searchQuery)
    }
  }, [globalState.searchQuery])

  useEffect(() => {
    if (!canLoadCompanyHeader) {
      setLibraryVesselOptions([])
      return
    }
    let cancelled = false
    listRiskLibraryVesselOptions(forCompanyGroupApi ?? null)
      .then((rows) => {
        if (!cancelled) setLibraryVesselOptions(Array.isArray(rows) ? rows : [])
      })
      .catch(() => {
        if (!cancelled) setLibraryVesselOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [canLoadCompanyHeader, forCompanyGroupApi])

  useEffect(() => {
    if (mainTab !== 'master' || !categories.length) return
    let cancelled = false
    ;(async () => {
      try {
        setMatrixLoading(true)
        const pairs = await Promise.all(
          categories.map(async (c) => {
            const t = await listRiskLibraryTasks(c.id, forCompanyGroupApi, forVesselApi)
            return [c.id, t] as const
          })
        )
        if (cancelled) return
        const m: Record<number, RiskLibraryTaskSummary[]> = {}
        for (const [id, t] of pairs) m[id] = t
        setMatrixByCat(m)
      } catch (e: any) {
        toast.error('Failed to load master list')
      } finally {
        if (!cancelled) setMatrixLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [mainTab, categories, forCompanyGroupApi, forVesselApi])

  useEffect(() => {
    if (!canLoadCompanyHeader) {
      setLibraryHeader({})
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const h = await getRiskLibraryCompanyHeader(forCompanyGroupApi)
        if (!cancelled) setLibraryHeader(h)
      } catch {
        if (!cancelled) setLibraryHeader({})
      }
    })()
    return () => {
      cancelled = true
    }
  }, [canLoadCompanyHeader, forCompanyGroupApi])

  const loadTasks = useCallback(
    async (categoryId: number, isCancelled: () => boolean = () => false) => {
      try {
        setTasksLoading(true)
        const t = await listRiskLibraryTasks(categoryId, forCompanyGroupApi, forVesselApi)
        if (!isCancelled()) setTasks(t)
      } catch (e: any) {
        if (!isCancelled()) toast.error('Failed to load tasks')
      } finally {
        if (!isCancelled()) setTasksLoading(false)
      }
    },
    [forCompanyGroupApi, forVesselApi]
  )

  useEffect(() => {
    if (mainTab !== 'category' || selectedCatId == null) return
    let cancelled = false
    ;(async () => {
      try {
        setSheetLoading(true)
        const d = await listRiskLibraryCategoryTaskDetails(selectedCatId, forCompanyGroupApi, forVesselApi)
        if (!cancelled) setSheetTasks(d)
      } catch (e: any) {
        toast.error('Failed to load category worksheet')
        if (!cancelled) setSheetTasks([])
      } finally {
        if (!cancelled) setSheetLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [mainTab, selectedCatId, forCompanyGroupApi, forVesselApi])

  useEffect(() => {
    let cancelled = false
    if (mainTab === 'category' && selectedCatId != null) loadTasks(selectedCatId, () => cancelled)
    return () => { cancelled = true }
  }, [mainTab, selectedCatId, loadTasks])

  useEffect(() => {
    if (detailId == null) {
      setDetail(null)
      setDetailModalEdit(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setDetailLoading(true)
        const d = await getRiskLibraryTask(detailId)
        if (!cancelled) setDetail(d)
      } catch (e: any) {
        toast.error('Failed to load task')
        if (!cancelled) setDetailId(null)
      } finally {
        if (!cancelled) setDetailLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [detailId])

  useEffect(() => {
    if (!detail || !detailModalEdit) return
    const w = detailToWritePayload(detail)
    setDmTitle(w.title)
    setDmWorkActivity(w.workActivity || '')
    if (detail.categoryCode === 'CRITICAL_EQUIPMENT') {
      const fl = w.failures && w.failures.length > 0 ? w.failures : [emptyFailure()]
      setDmFailures(
        fl.map((f) => ({
          ...f,
          hazardPoints: f.hazardPoints.length > 0 ? f.hazardPoints : [''],
          existingControlLines:
            f.existingControlLines.length > 0 ? f.existingControlLines : [''],
          furtherControlLines:
            f.furtherControlLines.length > 0 ? f.furtherControlLines : [''],
        }))
      )
      setDmHazards([emptyHazard()])
    } else {
      setDmHazards(
        w.hazards.length
          ? w.hazards.map((h) => ({
              ...h,
              existingControlLines:
                h.existingControlLines.length > 0 ? h.existingControlLines : [''],
              furtherControlLines:
                h.furtherControlLines.length > 0 ? h.furtherControlLines : [''],
            }))
          : [emptyHazard()]
      )
      setDmFailures([emptyFailure()])
    }
  }, [detail, detailModalEdit])

  const openHeader = async () => {
    if (!canLoadCompanyHeader) {
      toast.error('Select a company in the filter or use a company login to manage the header')
      return
    }
    try {
      setHeaderLoading(true)
      const h = await getRiskLibraryCompanyHeader(forCompanyGroupApi)
      setHeader(h)
      setHeaderOpen(true)
    } catch (e: any) {
      toast.error('Could not load header')
    } finally {
      setHeaderLoading(false)
    }
  }

  const saveHeader = async () => {
    if (!canEditCompanyHeader) {
      toast.error('You do not have permission to edit the risk library header.')
      return
    }
    try {
      setHeaderLoading(true)
      const { revision: _r, ...rest } = header
      const saved = await saveRiskLibraryCompanyHeader(rest, forCompanyGroupApi)
      setHeader(saved)
      toast.success('Header saved', { toastId: 'risk-lib-header-saved' })
      setHeaderOpen(false)
      const h2 = await getRiskLibraryCompanyHeader(forCompanyGroupApi)
      setLibraryHeader(h2)
    } catch (e: any) {
      toast.error('Save failed')
    } finally {
      setHeaderLoading(false)
    }
  }

  const canEditTaskSummary = (t: { globalDefault: boolean; vesselId?: number | null }) => {
    if (!permUpdate) return false
    if (t.globalDefault) return Boolean(permGlobal)
    if (shipboardUser && !t.vesselId) return false
    return true
  }

  const canDeleteTask = (t: RiskLibraryTaskSummary) => {
    if (!permDelete) return false
    if (t.globalDefault) return false
    if (shipboardUser && !t.vesselId) return false
    return true
  }

  const canPromoteTaskToCompany = (t: { globalDefault: boolean; vesselId?: number | null }) =>
    Boolean(permUpdate) && !t.globalDefault && t.vesselId != null && !shipboardUser

  const refreshLibraryLists = async () => {
    if (selectedCatId != null) {
      await loadTasks(selectedCatId)
      setSheetTasks(await listRiskLibraryCategoryTaskDetails(selectedCatId, forCompanyGroupApi, forVesselApi))
    }
    if (mainTab === 'master' && categories.length) {
      const pairs = await Promise.all(
        categories.map(async (c) => [c.id, await listRiskLibraryTasks(c.id, forCompanyGroupApi, forVesselApi)] as const)
      )
      const m: Record<number, RiskLibraryTaskSummary[]> = {}
      for (const [id, t] of pairs) m[id] = t
      setMatrixByCat(m)
    }
  }

  const handlePromoteToCompanyScope = async (taskId: number) => {
    try {
      await promoteRiskLibraryTaskToCompanyScope(taskId, forCompanyGroupApi ?? null)
      toast.success('Task is now company-wide for all vessels in this company group.', {
        toastId: 'risk-lib-promoted',
      })
      if (detailId === taskId) {
        const d = await getRiskLibraryTask(taskId)
        setDetail(d)
      }
      await refreshLibraryLists()
    } catch (e: any) {
      toast.error('Promote failed')
    }
  }

  const openNewTask = () => {
    if (selectedCatId == null) return
    if (!permCreate) {
      toast.error('You do not have permission to create risk library tasks.')
      return
    }
    if (shipboardUser && !currentUser?.vessel?.id) {
      toast.error('You must be assigned to a vessel to add vessel library tasks.')
      return
    }
    if (!shipboardUser && canManageGlobalLibraryUi && !superadminCompanyId && !editGlobal) {
      toast.error('Select a company to create a custom task for that company, or enable “global/default library task”.')
      return
    }
    setEditGlobal(false)
    setFormTitle('')
    setFormWorkActivity('')
    setFormHazards([emptyHazard()])
    setFormFailures([emptyFailure()])
    setFormScopeVesselId(forVesselApi ?? '')
    setEditOpen(true)
  }

  const submitTaskForm = async () => {
    if (selectedCatId == null) return
    try {
      setSaving(true)
      if (!formTitle.trim()) {
        toast.error(
          isCriticalCategory ? 'Equipment name (title) is required.' : 'Title is required.',
          { toastId: 'risk-lib-val-title' }
        )
        return
      }
      if (!isCriticalCategory && !formWorkActivity.trim()) {
        toast.error('Work activity is required.', { toastId: 'risk-lib-val-wa' })
        return
      }
      const optionalVesselScope: { vesselId?: number } = {}
      if (!editGlobal && !shipboardUser && typeof formScopeVesselId === 'number') {
        optionalVesselScope.vesselId = formScopeVesselId
      }
      let payload: RiskLibraryTaskWritePayload
      if (isCriticalCategory) {
        const failMsg = validateFailureRowsForSave(formFailures)
        if (failMsg) {
          toast.error(failMsg, { toastId: 'risk-lib-val-fail' })
          return
        }
        const failures: RiskLibraryTaskFailureWritePayload[] = formFailures.map((f, idx) => ({
          sortOrder: idx,
          failureText: f.failureText.trim(),
          likelihood: f.likelihood,
          severity: f.severity,
          riskFactor: f.riskFactor,
          hazardPoints: f.hazardPoints.map((x) => x.trim()).filter(Boolean),
          existingControlLines: f.existingControlLines.filter((x) => x.trim()),
          furtherControlLines: f.furtherControlLines.filter((x) => x.trim()),
        }))
        payload = {
          categoryId: selectedCatId,
          title: formTitle.trim(),
          workActivity: undefined,
          equipmentName: formTitle.trim() || undefined,
          failureMode: undefined,
          hazards: [],
          failures,
          globalDefault: permGlobal && editGlobal,
          companyGroupId:
            canManageGlobalLibraryUi && superadminCompanyId && !editGlobal ? superadminCompanyId : undefined,
          ...optionalVesselScope,
        }
      } else {
        const hazMsg = validateHazardRowsForSave(formHazards)
        if (hazMsg) {
          toast.error(hazMsg, { toastId: 'risk-lib-val-haz' })
          return
        }
        const hazards: RiskLibraryHazardWritePayload[] = formHazards.map((h, idx) => ({
          sortOrder: idx,
          hazardText: h.hazardText.trim(),
          likelihood: h.likelihood,
          severity: h.severity,
          riskFactor: h.riskFactor,
          existingControlLines: h.existingControlLines.filter((x) => x.trim()),
          furtherControlLines: h.furtherControlLines.filter((x) => x.trim()),
        }))
        payload = {
          categoryId: selectedCatId,
          title: formTitle.trim(),
          workActivity: formWorkActivity.trim() || undefined,
          equipmentName: undefined,
          failureMode: undefined,
          hazards,
          failures: [],
          globalDefault: permGlobal && editGlobal,
          companyGroupId:
            canManageGlobalLibraryUi && superadminCompanyId && !editGlobal ? superadminCompanyId : undefined,
          ...optionalVesselScope,
        }
      }
      await createRiskLibraryTask(payload)
      toast.success('Task created', { toastId: 'risk-lib-task-created' })
      setEditOpen(false)
      if (selectedCatId != null) loadTasks(selectedCatId)
      if (mainTab === 'master') {
        const pairs = await Promise.all(
          categories.map(async (c) => [c.id, await listRiskLibraryTasks(c.id, forCompanyGroupApi, forVesselApi)] as const)
        )
        const m: Record<number, RiskLibraryTaskSummary[]> = {}
        for (const [id, t] of pairs) m[id] = t
        setMatrixByCat(m)
      }
      if (mainTab === 'category' && selectedCatId != null) {
        setSheetTasks(await listRiskLibraryCategoryTaskDetails(selectedCatId, forCompanyGroupApi, forVesselApi))
      }
      if (detailId != null) {
        const d = await getRiskLibraryTask(detailId)
        setDetail(d)
      }
    } catch (e: any) {
      toast.error('Save failed')
    } finally {
      setSaving(false)
    }
  }

  const saveDetailModal = async () => {
    if (detail == null || selectedCatId == null) return
    if (!canEditTaskSummary(detail)) {
      toast.error('You do not have permission to edit this risk library task.')
      return
    }
    try {
      setDmSaving(true)
      if (!dmTitle.trim()) {
        toast.error(
          detail.categoryCode === 'CRITICAL_EQUIPMENT'
            ? 'Equipment name (title) is required.'
            : 'Title is required.',
          { toastId: 'risk-lib-dm-title' }
        )
        return
      }
      if (detail.categoryCode !== 'CRITICAL_EQUIPMENT' && !dmWorkActivity.trim()) {
        toast.error('Work activity is required.', { toastId: 'risk-lib-dm-wa' })
        return
      }
      let payload: RiskLibraryTaskWritePayload
      if (detail.categoryCode === 'CRITICAL_EQUIPMENT') {
        const failMsg = validateFailureRowsForSave(dmFailures)
        if (failMsg) {
          toast.error(failMsg, { toastId: 'risk-lib-dm-fail' })
          return
        }
        const failures: RiskLibraryTaskFailureWritePayload[] = dmFailures.map((f, idx) => ({
          sortOrder: idx,
          failureText: f.failureText.trim(),
          likelihood: f.likelihood,
          severity: f.severity,
          riskFactor: f.riskFactor,
          hazardPoints: f.hazardPoints.map((x) => x.trim()).filter(Boolean),
          existingControlLines: f.existingControlLines.filter((x) => x.trim()),
          furtherControlLines: f.furtherControlLines.filter((x) => x.trim()),
        }))
        payload = {
          categoryId: detail.categoryId,
          title: dmTitle.trim(),
          workActivity: undefined,
          equipmentName: dmTitle.trim() || undefined,
          failureMode: undefined,
          hazards: [],
          failures,
        }
      } else {
        const hazMsg = validateHazardRowsForSave(dmHazards)
        if (hazMsg) {
          toast.error(hazMsg, { toastId: 'risk-lib-dm-haz' })
          return
        }
        const hazards: RiskLibraryHazardWritePayload[] = dmHazards.map((h, idx) => ({
          sortOrder: idx,
          hazardText: h.hazardText.trim(),
          likelihood: h.likelihood,
          severity: h.severity,
          riskFactor: h.riskFactor,
          existingControlLines: h.existingControlLines.filter((x) => x.trim()),
          furtherControlLines: h.furtherControlLines.filter((x) => x.trim()),
        }))
        payload = {
          categoryId: detail.categoryId,
          title: dmTitle.trim(),
          workActivity: dmWorkActivity.trim() || undefined,
          equipmentName: undefined,
          failureMode: undefined,
          hazards,
          failures: [],
        }
      }
      await updateRiskLibraryTask(detail.id, payload)
      toast.success('Worksheet saved', { toastId: `risk-lib-task-updated-${detail.id}` })
      setDetailModalEdit(false)
      const d = await getRiskLibraryTask(detail.id)
      setDetail(d)
      if (selectedCatId != null) loadTasks(selectedCatId)
      if (mainTab === 'master') {
        const pairs = await Promise.all(
          categories.map(async (c) => [c.id, await listRiskLibraryTasks(c.id, forCompanyGroupApi, forVesselApi)] as const)
        )
        const m: Record<number, RiskLibraryTaskSummary[]> = {}
        for (const [id, t] of pairs) m[id] = t
        setMatrixByCat(m)
      }
      if (mainTab === 'category' && selectedCatId != null) {
        setSheetTasks(await listRiskLibraryCategoryTaskDetails(selectedCatId, forCompanyGroupApi, forVesselApi))
      }
    } catch (e: any) {
      toast.error('Save failed')
    } finally {
      setDmSaving(false)
    }
  }

  const removeTask = async (id: number) => {
    if (!window.confirm('Delete this custom task?')) return
    try {
      await deleteRiskLibraryTask(id)
      toast.success('Task deleted', { toastId: `risk-lib-task-deleted-${id}` })
      if (detailId === id) setDetailId(null)
      if (selectedCatId != null) loadTasks(selectedCatId)
      if (mainTab === 'master') {
        const pairs = await Promise.all(
          categories.map(async (c) => [c.id, await listRiskLibraryTasks(c.id, forCompanyGroupApi, forVesselApi)] as const)
        )
        const m: Record<number, RiskLibraryTaskSummary[]> = {}
        for (const [id2, t] of pairs) m[id2] = t
        setMatrixByCat(m)
      }
      if (mainTab === 'category' && selectedCatId != null) {
        setSheetTasks(await listRiskLibraryCategoryTaskDetails(selectedCatId, forCompanyGroupApi, forVesselApi))
      }
    } catch (e: any) {
      toast.error('Delete failed')
    }
  }

  const filteredTasks = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return tasks
    return tasks.filter(
      (t) =>
        t.title.toLowerCase().includes(q) || String(t.categoryTaskNumber).includes(q)
    )
  }, [tasks, search])

  const matrixRows = useMemo(() => {
    const cols = categories.map((c) => matrixByCat[c.id] || [])
    const maxLen = Math.max(0, ...cols.map((t) => t.length))
    return Array.from({ length: maxLen }, (_, rowIdx) =>
      categories.map((c) => {
        const list = matrixByCat[c.id] || []
        return list[rowIdx] ?? null
      })
    )
  }, [categories, matrixByCat])

  const showAddButton =
    (canAdd || permCreate) &&
    permCreate &&
    (!shipboardUser || currentUser?.vessel?.id) &&
    selectedCatId != null &&
    (shipboardUser || !canManageGlobalLibraryUi || superadminCompanyId != null || editGlobal)

  const revisionDisplay =
    libraryHeader.revision != null && libraryHeader.revision !== ''
      ? String(libraryHeader.revision)
      : '—'

  const openDetail = (id: number, asEdit: boolean) => {
    if (asEdit && !permUpdate) {
      toast.error('You do not have permission to edit this risk library task.')
      return
    }
    setDetailModalEdit(asEdit)
    setDetailId(id)
  }

  const closeDetail = () => {
    setDetailId(null)
    setDetailModalEdit(false)
  }

  const renderWorksheetActions = (task: RiskLibraryTaskDetail, rowCount: number) => (
    <td rowSpan={rowCount} className='align-top text-center'>
      <div className='d-flex justify-content-center gap-2 flex-nowrap py-2'>
        <Button
          variant='light'
          size='sm'
          aria-label={`View task ${task.categoryTaskNumber}`}
          onClick={() => openDetail(task.id, false)}
        >
          View
        </Button>
        {canEditTaskSummary(task) && (
          <Button
            variant='light-primary'
            size='sm'
            aria-label={`Edit task ${task.categoryTaskNumber}`}
            onClick={() => openDetail(task.id, true)}
          >
            Edit
          </Button>
        )}
      </div>
    </td>
  )

  const handleExportMasterPdf = () => {
    if (!permDownload) {
      toast.error('You do not have permission to export the risk library.')
      return
    }
    if (!canLoadCompanyHeader) {
      toast.error('Select a company to export merged global + custom tasks and the SAF header.', {
        toastId: 'risk-lib-export-master-need-co',
      })
      return
    }
    if (!categories.length) {
      toast.error('Categories are not loaded yet.')
      return
    }
    try {
      exportRiskLibraryMasterListPdf({
        companyName: headerEditingCompanyLabel,
        libraryHeader,
        categories,
        matrixByCat,
      })
      toast.success('Master list exported to PDF', { toastId: 'risk-lib-export-master-ok' })
    } catch (e: any) {
      console.error(e)
      toast.error('PDF export failed')
    }
  }

  const handleExportCategoryWorksheetPdf = () => {
    if (!permDownload) {
      toast.error('You do not have permission to export the risk library.')
      return
    }
    if (!canLoadCompanyHeader) {
      toast.error('Select a company to export the worksheet and SAF header.', {
        toastId: 'risk-lib-export-cat-need-co',
      })
      return
    }
    if (selectedCatId == null || !selectedCategory) {
      toast.error('Select a category first.')
      return
    }
    if (!sheetTasks.length) {
      toast.error('No worksheet rows loaded for this category yet.')
      return
    }
    try {
      exportRiskLibraryCategoryWorksheetPdf({
        companyName: headerEditingCompanyLabel,
        libraryHeader,
        categoryName: selectedCategory.name,
        categoryCode: selectedCategory.code,
        sheetTasks,
      })
      toast.success('Category worksheet exported to PDF', { toastId: 'risk-lib-export-cat-ok' })
    } catch (e: any) {
      console.error(e)
      toast.error('PDF export failed')
    }
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='fw-bold m-0'>Risk Assessment Form</h3>
        </div>
        <div className='card-toolbar gap-2 d-flex flex-wrap align-items-center'>
          {canManageGlobalLibraryUi && (
            <Form.Select
              size='sm'
              aria-label='Risk library company'
              style={{ minWidth: 220 }}
              value={superadminCompanyId ?? ''}
              onChange={(event) => {
                const id = Number(event.target.value)
                setCompany(companyOptions.find((company) => company.id === id) ?? null)
              }}
            >
              <option value=''>Company: defaults only (global tasks)</option>
              {companyOptions.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </Form.Select>
          )}
          <Button
            variant='light-primary'
            size='sm'
            onClick={openHeader}
            disabled={headerLoading || !canLoadCompanyHeader}
            title={!canLoadCompanyHeader ? 'Select a company in the filter or use a company login' : undefined}
          >
            Manage SAF header
          </Button>
          {mainTab === 'category' && showAddButton && (
            <Button variant='primary' size='sm' onClick={openNewTask}>
              Add custom task
            </Button>
          )}
        </div>
      </div>
      <div className='card-body pt-0'>
        {catLoading ? (
          <Spinner animation='border' size='sm' />
        ) : (
          <>
            <Nav variant='tabs' className='mb-4 nav-line-tabs'>
              <Nav.Item>
                <Nav.Link active={mainTab === 'master'} onClick={() => setMainTab('master')} style={{ cursor: 'pointer' }}>
                  Master list view
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={mainTab === 'category'}
                  onClick={() => setMainTab('category')}
                  style={{ cursor: 'pointer' }}
                >
                  Category view
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {mainTab === 'master' && (
              <>
                {canLoadCompanyHeader && (
                  <div
                    className='rounded border px-4 py-3 mb-4'
                    style={{ background: '#faf8f3', borderColor: '#c5a059' }}
                  >
                    <Row className='g-2 fs-7'>
                      <Col md={4}>
                        <span className='text-muted'>Document</span>
                        <div className='fw-semibold'>{libraryHeader.document || '—'}</div>
                      </Col>
                      <Col md={4}>
                        <span className='text-muted'>Valid from</span>
                        <div className='fw-semibold'>
                          {libraryHeader.validFrom
                            ? String(libraryHeader.validFrom).slice(0, 10)
                            : '—'}
                        </div>
                      </Col>
                      <Col md={4}>
                        <span className='text-muted'>Revision</span>
                        <div className='fw-semibold'>{revisionDisplay}</div>
                      </Col>
                      <Col md={4}>
                        <span className='text-muted'>Process</span>
                        <div className='fw-semibold'>{libraryHeader.process || '—'}</div>
                      </Col>
                      <Col md={4}>
                        <span className='text-muted'>Issuer</span>
                        <div className='fw-semibold'>{libraryHeader.issuer || '—'}</div>
                      </Col>
                      <Col md={12}>
                        <span className='text-muted'>Procedure</span>
                        <div className='fw-semibold'>{libraryHeader.procedure || '—'}</div>
                      </Col>
                    </Row>
                  </div>
                )}
                {!canLoadCompanyHeader && canManageGlobalLibraryUi && (
                  <div className='alert alert-warning mb-4 py-2'>
                    Select a company above to show that company&apos;s SAF header and merged master list (global +
                    custom tasks).
                  </div>
                )}
                <div className='d-flex justify-content-end mb-2'>
                  <Button
                    variant='light-primary'
                    size='sm'
                    onClick={handleExportMasterPdf}
                    disabled={!canLoadCompanyHeader || !permDownload || matrixLoading || !categories.length}
                    title={
                      !canLoadCompanyHeader
                        ? 'Pick a company in the filter (or use a company login) to export PDF with SAF header'
                        : 'Export master list (PDF, text tables)'
                    }
                  >
                    Export master list PDF
                  </Button>
                </div>
                <div className='position-relative table-responsive'>
                  {matrixLoading && (
                    <div className='position-absolute top-50 start-50 translate-middle z-1'>
                      <Spinner animation='border' size='sm' />
                    </div>
                  )}
                  <Table bordered size='sm' className='align-middle text-center mb-0'>
                    <thead>
                      <tr>
                        {categories.map((c) => (
                          <th
                            key={c.id}
                            colSpan={2}
                            className='text-white py-3'
                            style={{ background: categoryHeaderColor(c.code) }}
                          >
                            {c.name}
                          </th>
                        ))}
                      </tr>
                      <tr className='fw-semibold bg-light'>
                        {categories.map((c) => (
                          <React.Fragment key={c.id}>
                            <th style={{ width: '6%' }}>Task ID</th>
                            <th>Task</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrixLoading || catLoading ? (
                        <TableSkeleton rows={8} cols={categories.length ? categories.length * 2 : 6} />
                      ) : matrixRows.length === 0 ? (
                        <tr>
                          <td colSpan={categories.length * 2 || 6} className='text-muted py-6'>
                            No tasks loaded.
                          </td>
                        </tr>
                      ) : (
                        matrixRows.map((row, ri) => (
                          <tr key={ri}>
                            {row.map((cell, ci) => (
                              <React.Fragment key={`${ri}-${ci}`}>
                                <td className='text-muted'>
                                  {cell ? (
                                    <button
                                      type='button'
                                      className='btn btn-link btn-sm p-0 text-decoration-none'
                                      onClick={() => openDetail(cell.id, false)}
                                    >
                                      {cell.categoryTaskNumber}
                                    </button>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className='text-start'>
                                  {cell ? (
                                    <button
                                      type='button'
                                      className='btn btn-link p-0 text-start text-dark text-decoration-none'
                                      onClick={() => openDetail(cell.id, false)}
                                    >
                                      {cell.title}
                                    </button>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              </React.Fragment>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </>
            )}

            {mainTab === 'category' && (
              <>
                <div className='row mb-3 g-2 align-items-end'>
                  <div className='col-md-6'>
                    <Form.Label className='fw-semibold'>Category</Form.Label>
                    <Form.Select
                      value={selectedCatId ?? ''}
                      onChange={(e) => setSelectedCatId(e.target.value ? Number(e.target.value) : null)}
                    >
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className='col-md-4'>
                    <Form.Control
                      placeholder='Search by task name or ID…'
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <div className='col-md-2 text-md-end'>
                    {showAddButton && (
                      <Button variant='primary' size='sm' onClick={openNewTask}>
                        Add custom task
                      </Button>
                    )}
                  </div>
                </div>

                <div className='d-flex justify-content-end mb-2'>
                  <Button
                    variant='light-primary'
                    size='sm'
                    onClick={handleExportCategoryWorksheetPdf}
                    disabled={
                      !permDownload ||
                      !canLoadCompanyHeader ||
                      sheetLoading ||
                      selectedCatId == null ||
                      !sheetTasks.length
                    }
                    title={
                      !canLoadCompanyHeader
                        ? 'Pick a company in the filter (or use a company login) to export PDF with SAF header'
                        : 'Export category worksheet table only (PDF, text tables — not the task list below)'
                    }
                  >
                    Export category worksheet PDF
                  </Button>
                </div>

                <div className='position-relative table-responsive'>
                  {sheetLoading && (
                    <div className='position-absolute top-50 start-50 translate-middle z-1'>
                      <Spinner animation='border' size='sm' />
                    </div>
                  )}
                  <Table
                    bordered
                    size='sm'
                    aria-label='Category worksheet'
                    aria-busy={sheetLoading}
                    className='align-middle table-bordered border-secondary'
                    style={{ minWidth: selectedCategory?.code === 'CRITICAL_EQUIPMENT' ? 1450 : 1200 }}
                  >
                    <thead>
                      <tr
                        className='fw-bold text-uppercase text-center text-white'
                        style={{ background: '#b8892e', whiteSpace: 'nowrap' }}
                      >
                        <th>Task ID</th>
                        {selectedCategory?.code === 'CRITICAL_EQUIPMENT' ? (
                          <>
                            <th>Equipment</th>
                            <th>Failure</th>
                            <th>Hazard points</th>
                            <th>Existing control measures</th>
                            <th>Likelihood</th>
                            <th>Severity</th>
                            <th>Risk factor</th>
                            <th>Further controls</th>
                          </>
                        ) : (
                          <>
                            <th>Work activity</th>
                            <th>Hazard</th>
                            <th>Existing control measures</th>
                            <th>Likelihood</th>
                            <th>Severity</th>
                            <th>Risk factor</th>
                            <th>Further controls</th>
                          </>
                        )}
                        <th style={{ minWidth: 130 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sheetLoading ? (
                        <TableSkeleton rows={6} cols={isCriticalCategory ? 10 : 9} />
                      ) : sheetTasks.flatMap((task) => {
                        if (task.categoryCode === 'CRITICAL_EQUIPMENT') {
                          const failures =
                            task.failures && task.failures.length > 0
                              ? task.failures
                              : [
                                  {
                                    id: 0,
                                    sortOrder: 0,
                                    failureText: '—',
                                    likelihood: 'POSSIBLE' as const,
                                    severity: 'MINOR' as const,
                                    riskFactor: 'LOW' as const,
                                    hazardPoints: [{ sortOrder: 0, pointText: '—' }],
                                    existingControls: [],
                                    furtherControls: [],
                                  },
                                ]
                          const rc = Math.max(failures.length, 1)
                          return failures.map((f, fi) => (
                            <tr
                              key={`${task.id}-f-${f.id ?? fi}`}
                              style={{
                                background: task.id % 2 === 0 ? '#fcfdff' : '#fffef8',
                                borderTop: fi === 0 ? '3px solid #6c757d' : undefined,
                                borderBottom: fi === rc - 1 ? '2px solid #adb5bd' : undefined,
                              }}
                            >
                              {fi === 0 && (
                                <>
                                  <td rowSpan={rc} className='text-center fw-bold bg-light'>
                                    <button
                                      type='button'
                                      className='btn btn-link p-0 fw-bold'
                                      onClick={() => openDetail(task.id, false)}
                                    >
                                      {task.categoryTaskNumber}
                                    </button>
                                  </td>
                                  <td rowSpan={rc} className='fw-bold bg-light text-uppercase small'>
                                    {task.equipmentName || task.title}
                                  </td>
                                </>
                              )}
                              <td className='small fw-semibold'>{f.failureText}</td>
                              <td>
                                <ul className='mb-0 ps-3'>
                                  {(f.hazardPoints || []).length ? (
                                    (f.hazardPoints || []).map((p, pi) => (
                                      <li key={`hp-${task.id}-${fi}-${pi}`}>{p.pointText}</li>
                                    ))
                                  ) : (
                                    <li>—</li>
                                  )}
                                </ul>
                              </td>
                              <td>
                                <ul className='mb-0 ps-3'>
                                  {(f.existingControls || []).map((c) => (
                                    <li key={c.id}>{c.lineText}</li>
                                  ))}
                                </ul>
                              </td>
                              <td className='text-center'>
                                <span className={`badge ${likelihoodBadge(f.likelihood)}`}>
                                  Likelihood: {niceEnum(f.likelihood)}
                                </span>
                              </td>
                              <td className='text-center'>
                                <span className={`badge ${severityBadge(f.severity)}`}>
                                  Severity: {niceEnum(f.severity)}
                                </span>
                              </td>
                              <td className='text-center'>
                                <span className={`badge ${riskFactorBadge(f.riskFactor)}`}>
                                  Risk: {niceEnum(f.riskFactor)}
                                </span>
                              </td>
                              <td>
                                <ul className='mb-0 ps-3'>
                                  {(f.furtherControls || []).map((c) => (
                                    <li key={c.id}>{c.lineText}</li>
                                  ))}
                                </ul>
                              </td>
                              {fi === 0 && renderWorksheetActions(task, rc)}
                            </tr>
                          ))
                        }
                        const hazards = task.hazards?.length ? task.hazards : []
                        const rows =
                          hazards.length > 0
                            ? hazards
                            : [
                                {
                                  id: 0,
                                  sortOrder: 0,
                                  hazardText: '—',
                                  likelihood: 'POSSIBLE' as const,
                                  severity: 'MINOR' as const,
                                  riskFactor: 'LOW' as const,
                                  existingControls: [],
                                  furtherControls: [],
                                },
                              ]
                        const rc = Math.max(rows.length, 1)
                        return rows.map((h, hi) => (
                          <tr
                            key={`${task.id}-${h.id ?? hi}`}
                            style={{
                              background: task.id % 2 === 0 ? '#fcfdff' : '#fffef8',
                              borderTop: hi === 0 ? '3px solid #6c757d' : undefined,
                              borderBottom: hi === rc - 1 ? '2px solid #adb5bd' : undefined,
                            }}
                          >
                            {hi === 0 && (
                              <>
                                <td rowSpan={rc} className='text-center fw-bold bg-light'>
                                  <button
                                    type='button'
                                    className='btn btn-link p-0 fw-bold'
                                    onClick={() => openDetail(task.id, false)}
                                  >
                                    {task.categoryTaskNumber}
                                  </button>
                                </td>
                                <td rowSpan={rc} className='fw-bold bg-light text-uppercase small'>
                                  {task.workActivity || task.title}
                                </td>
                              </>
                            )}
                            <td>{h.hazardText}</td>
                            <td>
                              <ul className='mb-0 ps-3'>
                                {(h.existingControls || []).map((c) => (
                                  <li key={c.id}>{c.lineText}</li>
                                ))}
                              </ul>
                            </td>
                            <td className='text-center'>
                              <span className={`badge ${likelihoodBadge(h.likelihood)}`}>
                                Likelihood: {niceEnum(h.likelihood)}
                              </span>
                            </td>
                            <td className='text-center'>
                              <span className={`badge ${severityBadge(h.severity)}`}>
                                Severity: {niceEnum(h.severity)}
                              </span>
                            </td>
                            <td className='text-center'>
                              <span className={`badge ${riskFactorBadge(h.riskFactor)}`}>
                                Risk: {niceEnum(h.riskFactor)}
                              </span>
                            </td>
                            <td>
                              <ul className='mb-0 ps-3'>
                                {(h.furtherControls || []).map((c) => (
                                  <li key={c.id}>{c.lineText}</li>
                                ))}
                              </ul>
                            </td>
                            {hi === 0 && renderWorksheetActions(task, rc)}
                          </tr>
                        ))
                      })}
                    </tbody>
                  </Table>
                  {!sheetTasks.length && !sheetLoading && (
                    <div className='text-muted py-6 text-center'>No tasks in this category.</div>
                  )}
                </div>

                <div className='fw-bold text-muted mb-2 mt-6'>Task list &amp; actions</div>
                <Table hover className='table-row-dashed table-row-gray-300 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted border-bottom-0'>
                      <th>Task ID</th>
                      <th>Task name</th>
                      <th>Scope</th>
                      <th>Version</th>
                      <th className='text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasksLoading || sheetLoading ? (
                      <TableSkeleton rows={6} cols={5} />
                    ) : filteredTasks.length === 0 ? (
                      <tr>
                        <td colSpan={5} className='text-muted py-6 text-center'>
                          No tasks in this category.
                        </td>
                      </tr>
                    ) : (
                      filteredTasks.map((t) => (
                      <tr key={`${t.id}-${t.globalDefault ? 'g' : 'c'}`}>
                        <td className='align-middle'>{t.categoryTaskNumber}</td>
                        <td className='align-middle'>{t.title}</td>
                        <td className='align-middle'>
                          {t.globalDefault ? (
                            <span className='badge badge-light-primary'>Default</span>
                          ) : t.vesselId ? (
                            <span className='badge badge-light-info text-dark'>
                              Vessel{t.vesselFleetName ? `: ${t.vesselFleetName}` : ''}
                            </span>
                          ) : (
                            <span className='badge badge-light-warning text-dark'>Company</span>
                          )}
                        </td>
                        <td className='align-middle'>{formatRiskLibraryTaskVersion(t.taskVersion)}</td>
                        <td className='text-end align-middle'>
                          <Button variant='light' size='sm' className='me-1' onClick={() => openDetail(t.id, false)}>
                            View
                          </Button>
                          {canEditTaskSummary(t) && (
                            <Button
                              variant='light-primary'
                              size='sm'
                              className='me-1'
                              onClick={() => openDetail(t.id, true)}
                            >
                              Edit
                            </Button>
                          )}
                          {canDeleteTask(t) && (canDelete || permDelete) && (
                            <Button variant='light-danger' size='sm' onClick={() => removeTask(t.id)}>
                              Delete
                            </Button>
                          )}
                          {canPromoteTaskToCompany(t) && (canEdit || permUpdate) && (
                            <Button
                              variant='outline-primary'
                              size='sm'
                              className='ms-1'
                              onClick={() => handlePromoteToCompanyScope(t.id)}
                            >
                              Promote
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </div>

      {/* Detail / edit worksheet modal */}
      <Modal show={detailId != null} onHide={closeDetail} size='xl' scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {detail ? (
              <>
                Task {detail.categoryTaskNumber}: {detail.title}{' '}
                <span
                  className={
                    detail.globalDefault
                      ? 'badge badge-light-primary ms-2'
                      : 'badge badge-light-warning text-dark ms-2'
                  }
                >
                  {detail.globalDefault ? 'Default' : 'Custom'}
                </span>
                {!detail.globalDefault && detail.vesselFleetName && (
                  <span className='badge badge-light-info text-dark ms-2'>Vessel: {detail.vesselFleetName}</span>
                )}
                <span className='ms-2 text-muted fs-7'>v{formatRiskLibraryTaskVersion(detail.taskVersion)}</span>
                {detail.updatedAt && (
                  <span className='ms-2 text-muted fs-8 d-block'>
                    Updated {formatRiskLibraryDateOnly(detail.updatedAt)}
                  </span>
                )}
              </>
            ) : (
              'Task detail'
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={libraryModalBodyScrollStyle}>
          {detailLoading && <Spinner animation='border' size='sm' />}
          {detail && !detailLoading && (!detailModalEdit || !canEditTaskSummary(detail)) && (
            <RiskLibraryTaskDetailReadOnlyView
              detail={detail}
              topBar={
                <div className='d-flex justify-content-end gap-2 mb-2 flex-wrap'>
                  {canPromoteTaskToCompany(detail) && (canEdit || permUpdate) && (
                    <Button size='sm' variant='outline-primary' onClick={() => handlePromoteToCompanyScope(detail.id)}>
                      Promote to company-wide
                    </Button>
                  )}
                  {canEditTaskSummary(detail) && (
                    <Button size='sm' variant='primary' onClick={() => setDetailModalEdit(true)}>
                      Edit task
                    </Button>
                  )}
                </div>
              }
            />
          )}
          {detail && !detailLoading && detailModalEdit && canEditTaskSummary(detail) && (
            <>
              <div className='d-flex justify-content-between mb-2'>
                <span className='text-muted fs-7'>Editing — save to bump version</span>
                <Button size='sm' variant='light' onClick={() => setDetailModalEdit(false)}>
                  Cancel edit
                </Button>
              </div>
              {detail.categoryCode === 'CRITICAL_EQUIPMENT' ? (
                <>
                  <Form.Group className='mb-2'>
                    <Form.Label className={FORM_LABEL}>
                      Title <span className='text-danger'>*</span>{' '}
                      <span className='text-muted fw-normal fs-7'>(equipment name)</span>
                    </Form.Label>
                    <Form.Control value={dmTitle} onChange={(e) => setDmTitle(e.target.value)} />
                  </Form.Group>
                  <Form.Group className='mb-2'>
                    <Form.Label className={FORM_LABEL}>Equipment</Form.Label>
                    <Form.Control readOnly value={dmTitle || '—'} />
                  </Form.Group>
                  <div className='fw-bold mt-3 mb-3 pb-2 border-bottom text-primary'>Failures</div>
                  {dmFailures.map((f, fi) => (
                    <div key={fi} className='border rounded p-3 mb-3' style={sectionCardStyle(fi, 'failure')}>
                      <div className='d-flex justify-content-between mb-2'>
                        <span className='fw-semibold'>Failure {fi + 1}</span>
                        <Button
                          variant='link'
                          size='sm'
                          className='text-danger p-0'
                          onClick={() => setDmFailures(dmFailures.filter((_, i) => i !== fi))}
                        >
                          Remove
                        </Button>
                      </div>
                      <Form.Group className='mb-2'>
                        <Form.Label className={FORM_LABEL}>
                          Failure description <span className='text-danger'>*</span>
                        </Form.Label>
                        <Form.Control
                          as='textarea'
                          rows={2}
                          value={f.failureText}
                          onChange={(e) => {
                            const next = [...dmFailures]
                            next[fi] = { ...next[fi], failureText: e.target.value }
                            setDmFailures(next)
                          }}
                        />
                      </Form.Group>
                      <Form.Label className={FORM_LABEL}>
                        Hazard points <span className='text-danger'>*</span>
                      </Form.Label>
                      {f.hazardPoints.map((pt, pi) => (
                        <div key={pi} className='d-flex gap-1 mb-1'>
                          <Form.Control
                            value={pt}
                            onChange={(e) => {
                              const next = [...dmFailures]
                              const pts = [...next[fi].hazardPoints]
                              pts[pi] = e.target.value
                              next[fi] = { ...next[fi], hazardPoints: pts }
                              setDmFailures(next)
                            }}
                          />
                          <Button
                            variant='outline-danger'
                            size='sm'
                            onClick={() => {
                              const next = [...dmFailures]
                              next[fi].hazardPoints = next[fi].hazardPoints.filter((_, i) => i !== pi)
                              setDmFailures(next)
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant='light'
                        size='sm'
                        className='mb-3'
                        onClick={() => {
                          const next = [...dmFailures]
                          next[fi].hazardPoints = [...next[fi].hazardPoints, '']
                          setDmFailures(next)
                        }}
                      >
                        + Add hazard point
                      </Button>
                      <div className='row'>
                        <div className='col-md-4 mb-2'>
                          <Form.Label className={FORM_LABEL}>Likelihood</Form.Label>
                          <Form.Select
                            value={f.likelihood}
                            onChange={(e) => {
                              const next = [...dmFailures]
                              next[fi] = { ...next[fi], likelihood: e.target.value as any }
                              setDmFailures(next)
                            }}
                          >
                            {LIKELIHOOD_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {niceEnum(opt)}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                        <div className='col-md-4 mb-2'>
                          <Form.Label className={FORM_LABEL}>Severity</Form.Label>
                          <Form.Select
                            value={f.severity}
                            onChange={(e) => {
                              const next = [...dmFailures]
                              next[fi] = { ...next[fi], severity: e.target.value as any }
                              setDmFailures(next)
                            }}
                          >
                            {SEVERITY_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {niceEnum(opt)}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                        <div className='col-md-4 mb-2'>
                          <Form.Label className={FORM_LABEL}>Risk factor</Form.Label>
                          <Form.Select
                            value={f.riskFactor}
                            onChange={(e) => {
                              const next = [...dmFailures]
                              next[fi] = { ...next[fi], riskFactor: e.target.value as any }
                              setDmFailures(next)
                            }}
                          >
                            {RISK_FACTOR_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {niceEnum(opt)}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </div>
                      <Form.Label className={FORM_LABEL}>Existing control measures</Form.Label>
                      {f.existingControlLines.map((line, li) => (
                        <div key={li} className='d-flex gap-1 mb-1'>
                          <Form.Control
                            value={line}
                            onChange={(e) => {
                              const next = [...dmFailures]
                              const lines = [...next[fi].existingControlLines]
                              lines[li] = e.target.value
                              next[fi] = { ...next[fi], existingControlLines: lines }
                              setDmFailures(next)
                            }}
                          />
                          <Button
                            variant='outline-danger'
                            size='sm'
                            onClick={() => {
                              const next = [...dmFailures]
                              next[fi].existingControlLines = next[fi].existingControlLines.filter(
                                (_, i) => i !== li
                              )
                              setDmFailures(next)
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant='light'
                        size='sm'
                        className='mb-3 d-block'
                        onClick={() => {
                          const next = [...dmFailures]
                          next[fi].existingControlLines = [...next[fi].existingControlLines, '']
                          setDmFailures(next)
                        }}
                      >
                        + Add existing control measure
                      </Button>
                      <Form.Label className={`${FORM_LABEL} d-block`}>Further control measures</Form.Label>
                      {f.furtherControlLines.map((line, li) => (
                        <div key={li} className='d-flex gap-1 mb-1'>
                          <Form.Control
                            value={line}
                            onChange={(e) => {
                              const next = [...dmFailures]
                              const lines = [...next[fi].furtherControlLines]
                              lines[li] = e.target.value
                              next[fi] = { ...next[fi], furtherControlLines: lines }
                              setDmFailures(next)
                            }}
                          />
                          <Button
                            variant='outline-danger'
                            size='sm'
                            onClick={() => {
                              const next = [...dmFailures]
                              next[fi].furtherControlLines = next[fi].furtherControlLines.filter(
                                (_, i) => i !== li
                              )
                              setDmFailures(next)
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant='light'
                        size='sm'
                        className='d-block'
                        onClick={() => {
                          const next = [...dmFailures]
                          next[fi].furtherControlLines = [...next[fi].furtherControlLines, '']
                          setDmFailures(next)
                        }}
                      >
                        + Add further control measure
                      </Button>
                    </div>
                  ))}
                  <Button variant='light-primary' size='sm' onClick={() => setDmFailures([...dmFailures, emptyFailure()])}>
                    + Add failure
                  </Button>
                </>
              ) : (
                <>
                  <Form.Group className='mb-2'>
                    <Form.Label className={FORM_LABEL}>
                      Title <span className='text-danger'>*</span>
                    </Form.Label>
                    <Form.Control value={dmTitle} onChange={(e) => setDmTitle(e.target.value)} />
                  </Form.Group>
                  <Form.Group className='mb-2'>
                    <Form.Label className={FORM_LABEL}>
                      Work activity <span className='text-danger'>*</span>
                    </Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={2}
                      value={dmWorkActivity}
                      onChange={(e) => setDmWorkActivity(e.target.value)}
                    />
                  </Form.Group>
                  <div className='fw-bold mt-3 mb-3 pb-2 border-bottom text-warning'>Hazards</div>
                  {dmHazards.map((h, hi) => (
                    <div key={hi} className='border rounded p-3 mb-3' style={sectionCardStyle(hi, 'hazard')}>
                      <div className='d-flex justify-content-between mb-2'>
                        <span className='fw-semibold'>Hazard {hi + 1}</span>
                        <Button
                          variant='link'
                          size='sm'
                          className='text-danger p-0'
                          onClick={() => setDmHazards(dmHazards.filter((_, i) => i !== hi))}
                        >
                          Remove
                        </Button>
                      </div>
                      <Form.Group className='mb-2'>
                        <Form.Label className={FORM_LABEL}>
                          Hazard <span className='text-danger'>*</span>
                        </Form.Label>
                        <Form.Control
                          as='textarea'
                          rows={2}
                          value={h.hazardText}
                          onChange={(e) => {
                            const next = [...dmHazards]
                            next[hi] = { ...next[hi], hazardText: e.target.value }
                            setDmHazards(next)
                          }}
                        />
                      </Form.Group>
                      <div className='row'>
                        <div className='col-md-4 mb-2'>
                          <Form.Label className={FORM_LABEL}>Likelihood</Form.Label>
                          <Form.Select
                            value={h.likelihood}
                            onChange={(e) => {
                              const next = [...dmHazards]
                              next[hi] = { ...next[hi], likelihood: e.target.value as any }
                              setDmHazards(next)
                            }}
                          >
                            {LIKELIHOOD_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {niceEnum(opt)}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                        <div className='col-md-4 mb-2'>
                          <Form.Label className={FORM_LABEL}>Severity</Form.Label>
                          <Form.Select
                            value={h.severity}
                            onChange={(e) => {
                              const next = [...dmHazards]
                              next[hi] = { ...next[hi], severity: e.target.value as any }
                              setDmHazards(next)
                            }}
                          >
                            {SEVERITY_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {niceEnum(opt)}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                        <div className='col-md-4 mb-2'>
                          <Form.Label className={FORM_LABEL}>Risk factor</Form.Label>
                          <Form.Select
                            value={h.riskFactor}
                            onChange={(e) => {
                              const next = [...dmHazards]
                              next[hi] = { ...next[hi], riskFactor: e.target.value as any }
                              setDmHazards(next)
                            }}
                          >
                            {RISK_FACTOR_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {niceEnum(opt)}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </div>
                      <Form.Label className={FORM_LABEL}>Existing control measures</Form.Label>
                      {h.existingControlLines.map((line, li) => (
                        <div key={li} className='d-flex gap-1 mb-1'>
                          <Form.Control
                            value={line}
                            onChange={(e) => {
                              const next = [...dmHazards]
                              const lines = [...next[hi].existingControlLines]
                              lines[li] = e.target.value
                              next[hi] = { ...next[hi], existingControlLines: lines }
                              setDmHazards(next)
                            }}
                          />
                          <Button
                            variant='outline-danger'
                            size='sm'
                            onClick={() => {
                              const next = [...dmHazards]
                              next[hi].existingControlLines = next[hi].existingControlLines.filter(
                                (_, i) => i !== li
                              )
                              setDmHazards(next)
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant='light'
                        size='sm'
                        className='mb-3 d-block'
                        onClick={() => {
                          const next = [...dmHazards]
                          next[hi].existingControlLines = [...next[hi].existingControlLines, '']
                          setDmHazards(next)
                        }}
                      >
                        + Add existing control measure
                      </Button>
                      <Form.Label className={`${FORM_LABEL} d-block`}>Further control measures</Form.Label>
                      {h.furtherControlLines.map((line, li) => (
                        <div key={li} className='d-flex gap-1 mb-1'>
                          <Form.Control
                            value={line}
                            onChange={(e) => {
                              const next = [...dmHazards]
                              const lines = [...next[hi].furtherControlLines]
                              lines[li] = e.target.value
                              next[hi] = { ...next[hi], furtherControlLines: lines }
                              setDmHazards(next)
                            }}
                          />
                          <Button
                            variant='outline-danger'
                            size='sm'
                            onClick={() => {
                              const next = [...dmHazards]
                              next[hi].furtherControlLines = next[hi].furtherControlLines.filter(
                                (_, i) => i !== li
                              )
                              setDmHazards(next)
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant='light'
                        size='sm'
                        className='d-block'
                        onClick={() => {
                          const next = [...dmHazards]
                          next[hi].furtherControlLines = [...next[hi].furtherControlLines, '']
                          setDmHazards(next)
                        }}
                      >
                        + Add further control measure
                      </Button>
                    </div>
                  ))}
                  <Button variant='light-primary' size='sm' onClick={() => setDmHazards([...dmHazards, emptyHazard()])}>
                    + Add hazard
                  </Button>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={stickyModalFooterStyle}>
          <Button variant='secondary' onClick={closeDetail}>
            Close
          </Button>
          {detail && !detailLoading && detailModalEdit && canEditTaskSummary(detail) && (
            <Button variant='primary' onClick={saveDetailModal} disabled={dmSaving}>
              {dmSaving ? 'Submitting…' : 'Submit changes'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Header modal */}
      <Modal show={headerOpen} onHide={() => setHeaderOpen(false)} size='lg' scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Risk Assessment Header</Modal.Title>
        </Modal.Header>
        <Modal.Body style={libraryModalBodyScrollStyle}>
          <div className='small text-muted mb-3 pb-2 border-bottom'>
            <span className='fw-semibold text-dark'>Company for this header:</span>{' '}
            <span className='text-dark'>{headerEditingCompanyLabel}</span>
          </div>
          <Form.Group className='mb-2'>
            <Form.Label className='form-label fw-semibold fs-6 mb-2 text-dark'>Document</Form.Label>
            <Form.Control
              value={header.document || ''}
              onChange={(e) => setHeader({ ...header, document: e.target.value })}
            />
          </Form.Group>
          <Form.Group className='mb-2'>
            <Form.Label className='form-label fw-semibold fs-6 mb-2 text-dark'>Valid from</Form.Label>
            <Form.Control
              type='date'
              value={header.validFrom?.toString().slice(0, 10) || ''}
              onChange={(e) => setHeader({ ...header, validFrom: e.target.value || null })}
            />
          </Form.Group>
          <Form.Group className='mb-2'>
            <Form.Label className='form-label fw-semibold fs-6 mb-2 text-dark'>Process</Form.Label>
            <Form.Control
              value={header.process || ''}
              onChange={(e) => setHeader({ ...header, process: e.target.value })}
            />
          </Form.Group>
          <Form.Group className='mb-2'>
            <Form.Label className='form-label fw-semibold fs-6 mb-2 text-dark'>Issuer</Form.Label>
            <Form.Control
              value={header.issuer || ''}
              onChange={(e) => setHeader({ ...header, issuer: e.target.value })}
            />
          </Form.Group>
          <Form.Group className='mb-2'>
            <Form.Label className='form-label fw-semibold fs-6 mb-2 text-dark'>Procedure</Form.Label>
            <Form.Control
              as='textarea'
              rows={2}
              value={header.procedure || ''}
              onChange={(e) => setHeader({ ...header, procedure: e.target.value })}
            />
          </Form.Group>
          <Form.Group className='mb-2'>
            <Form.Label className='form-label fw-semibold fs-6 mb-2 text-dark'>Revision (read-only)</Form.Label>
            <Form.Control readOnly value={header.revision != null ? String(header.revision) : '0.001'} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setHeaderOpen(false)}>
            Close
          </Button>
          <Button variant='primary' onClick={saveHeader} disabled={headerLoading || !canEditCompanyHeader}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create custom / global task — standard categories vs Critical Equipment worksheet */}
      <Modal show={editOpen} onHide={() => setEditOpen(false)} size='lg' scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {isCriticalCategory ? 'Add critical equipment task' : 'Add task'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={libraryModalBodyScrollStyle}>
          {canManageGlobalLibraryUi && (
            <Form.Check
              type='checkbox'
              className='mb-3'
              id='rl-global'
              label='Create as global/default library task (visible to all companies and roles)'
              checked={editGlobal}
              onChange={(e) => setEditGlobal(e.target.checked)}
            />
          )}
          {shipboardUser && !editGlobal && (
            <Alert variant='secondary' className='py-2 fs-7 mb-3'>
              New tasks are saved for <strong>your vessel only</strong>. Your company can promote a task to apply fleet-wide.
            </Alert>
          )}
          {!editGlobal && canLoadCompanyHeader && !shipboardUser && (
            <Form.Group className='mb-3'>
              <Form.Label className={FORM_LABEL}>Applies to</Form.Label>
              <Form.Select
                value={formScopeVesselId === '' ? '' : String(formScopeVesselId)}
                onChange={(e) => {
                  const v = e.target.value
                  setFormScopeVesselId(v === '' ? '' : Number(v))
                }}
              >
                <option value=''>Whole company (all vessels)</option>
                {libraryVesselOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.fleetName}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className='text-muted'>Pick a vessel to draft a worksheet only that ship will see until promoted.</Form.Text>
            </Form.Group>
          )}
          {isCriticalCategory && (
            <Alert variant='info' className='py-2 fs-7 mb-3'>
              Critical equipment uses <strong>failures</strong>: each failure has multiple <strong>hazard points</strong>{' '}
              (bullets), one set of likelihood / severity / risk factor, and existing / further controls for that failure.
              This differs from other categories, where each hazard row carries its own risk fields.
            </Alert>
          )}
          <Form.Group className='mb-2'>
            <Form.Label className={FORM_LABEL}>
              {isCriticalCategory ? (
                <>
                  Title <span className='text-danger'>*</span>{' '}
                  <span className='text-muted fw-normal fs-7'>(equipment name)</span>
                </>
              ) : (
                <>
                  Title <span className='text-danger'>*</span>
                </>
              )}
            </Form.Label>
            <Form.Control value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
          </Form.Group>
          {isCriticalCategory ? (
            <>
              <Form.Group className='mb-2'>
                <Form.Label className={FORM_LABEL}>Equipment</Form.Label>
                <Form.Control readOnly value={formTitle || '—'} />
              </Form.Group>
              <div className='fw-bold mt-4 mb-3 pb-2 border-bottom text-primary'>Failures</div>
              {formFailures.map((f, fi) => (
                <div key={fi} className='border rounded p-3 mb-3' style={sectionCardStyle(fi, 'failure')}>
                  <div className='d-flex justify-content-between mb-2'>
                    <span className='fw-semibold'>Failure {fi + 1}</span>
                    <Button
                      variant='link'
                      size='sm'
                      className='text-danger p-0'
                      onClick={() => setFormFailures(formFailures.filter((_, i) => i !== fi))}
                    >
                      Remove
                    </Button>
                  </div>
                  <Form.Group className='mb-2'>
                    <Form.Label className={FORM_LABEL}>
                      Failure description <span className='text-danger'>*</span>
                    </Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={2}
                      value={f.failureText}
                      onChange={(e) => {
                        const next = [...formFailures]
                        next[fi] = { ...next[fi], failureText: e.target.value }
                        setFormFailures(next)
                      }}
                    />
                  </Form.Group>
                  <Form.Label className={FORM_LABEL}>
                    Hazard points <span className='text-danger'>*</span>
                  </Form.Label>
                  {f.hazardPoints.map((pt, pi) => (
                    <div key={pi} className='d-flex gap-1 mb-1'>
                      <Form.Control
                        value={pt}
                        onChange={(e) => {
                          const next = [...formFailures]
                          const pts = [...next[fi].hazardPoints]
                          pts[pi] = e.target.value
                          next[fi] = { ...next[fi], hazardPoints: pts }
                          setFormFailures(next)
                        }}
                      />
                      <Button
                        variant='outline-danger'
                        size='sm'
                        onClick={() => {
                          const next = [...formFailures]
                          next[fi].hazardPoints = next[fi].hazardPoints.filter((_, i) => i !== pi)
                          setFormFailures(next)
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant='light'
                    size='sm'
                    className='mb-3'
                    onClick={() => {
                      const next = [...formFailures]
                      next[fi].hazardPoints = [...next[fi].hazardPoints, '']
                      setFormFailures(next)
                    }}
                  >
                    + Add hazard point
                  </Button>
                  <div className='row'>
                    <div className='col-md-4 mb-2'>
                      <Form.Label className={FORM_LABEL}>Likelihood</Form.Label>
                      <Form.Select
                        value={f.likelihood}
                        onChange={(e) => {
                          const next = [...formFailures]
                          next[fi] = { ...next[fi], likelihood: e.target.value as any }
                          setFormFailures(next)
                        }}
                      >
                        {LIKELIHOOD_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {niceEnum(opt)}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className='col-md-4 mb-2'>
                      <Form.Label className={FORM_LABEL}>Severity</Form.Label>
                      <Form.Select
                        value={f.severity}
                        onChange={(e) => {
                          const next = [...formFailures]
                          next[fi] = { ...next[fi], severity: e.target.value as any }
                          setFormFailures(next)
                        }}
                      >
                        {SEVERITY_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {niceEnum(opt)}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className='col-md-4 mb-2'>
                      <Form.Label className={FORM_LABEL}>Risk factor</Form.Label>
                      <Form.Select
                        value={f.riskFactor}
                        onChange={(e) => {
                          const next = [...formFailures]
                          next[fi] = { ...next[fi], riskFactor: e.target.value as any }
                          setFormFailures(next)
                        }}
                      >
                        {RISK_FACTOR_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {niceEnum(opt)}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                  <Form.Label className={FORM_LABEL}>Existing control measures</Form.Label>
                  {f.existingControlLines.map((line, li) => (
                    <div key={li} className='d-flex gap-1 mb-1'>
                      <Form.Control
                        value={line}
                        onChange={(e) => {
                          const next = [...formFailures]
                          const lines = [...next[fi].existingControlLines]
                          lines[li] = e.target.value
                          next[fi] = { ...next[fi], existingControlLines: lines }
                          setFormFailures(next)
                        }}
                      />
                      <Button
                        variant='outline-danger'
                        size='sm'
                        onClick={() => {
                          const next = [...formFailures]
                          next[fi].existingControlLines = next[fi].existingControlLines.filter(
                            (_, i) => i !== li
                          )
                          setFormFailures(next)
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant='light'
                    size='sm'
                    className='mb-3 d-block'
                    onClick={() => {
                      const next = [...formFailures]
                      next[fi].existingControlLines = [...next[fi].existingControlLines, '']
                      setFormFailures(next)
                    }}
                  >
                    + Add existing control measure
                  </Button>
                  <Form.Label className={`${FORM_LABEL} d-block`}>Further control measures</Form.Label>
                  {f.furtherControlLines.map((line, li) => (
                    <div key={li} className='d-flex gap-1 mb-1'>
                      <Form.Control
                        value={line}
                        onChange={(e) => {
                          const next = [...formFailures]
                          const lines = [...next[fi].furtherControlLines]
                          lines[li] = e.target.value
                          next[fi] = { ...next[fi], furtherControlLines: lines }
                          setFormFailures(next)
                        }}
                      />
                      <Button
                        variant='outline-danger'
                        size='sm'
                        onClick={() => {
                          const next = [...formFailures]
                          next[fi].furtherControlLines = next[fi].furtherControlLines.filter(
                            (_, i) => i !== li
                          )
                          setFormFailures(next)
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant='light'
                    size='sm'
                    className='d-block'
                    onClick={() => {
                      const next = [...formFailures]
                      next[fi].furtherControlLines = [...next[fi].furtherControlLines, '']
                      setFormFailures(next)
                    }}
                  >
                    + Add further control measure
                  </Button>
                </div>
              ))}
              <Button
                variant='light-primary'
                size='sm'
                onClick={() => setFormFailures([...formFailures, emptyFailure()])}
              >
                + Add failure
              </Button>
            </>
          ) : (
            <>
              <Form.Group className='mb-2'>
                <Form.Label className={FORM_LABEL}>
                  Work activity <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={formWorkActivity}
                  onChange={(e) => setFormWorkActivity(e.target.value)}
                />
              </Form.Group>
              <div className='fw-bold mt-4 mb-3 pb-2 border-bottom text-warning'>Hazards</div>
              {formHazards.map((h, hi) => (
                <div key={hi} className='border rounded p-3 mb-3' style={sectionCardStyle(hi, 'hazard')}>
                  <div className='d-flex justify-content-between mb-2'>
                    <span className='fw-semibold'>Hazard {hi + 1}</span>
                    <Button
                      variant='link'
                      size='sm'
                      className='text-danger p-0'
                      onClick={() => setFormHazards(formHazards.filter((_, i) => i !== hi))}
                    >
                      Remove
                    </Button>
                  </div>
                  <Form.Group className='mb-2'>
                    <Form.Label className={FORM_LABEL}>
                      Hazard <span className='text-danger'>*</span>
                    </Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={2}
                      value={h.hazardText}
                      onChange={(e) => {
                        const next = [...formHazards]
                        next[hi] = { ...next[hi], hazardText: e.target.value }
                        setFormHazards(next)
                      }}
                    />
                  </Form.Group>
                  <div className='row'>
                    <div className='col-md-4 mb-2'>
                      <Form.Label className={FORM_LABEL}>Likelihood</Form.Label>
                      <Form.Select
                        value={h.likelihood}
                        onChange={(e) => {
                          const next = [...formHazards]
                          next[hi] = { ...next[hi], likelihood: e.target.value as any }
                          setFormHazards(next)
                        }}
                      >
                        {LIKELIHOOD_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {niceEnum(opt)}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className='col-md-4 mb-2'>
                      <Form.Label className={FORM_LABEL}>Severity</Form.Label>
                      <Form.Select
                        value={h.severity}
                        onChange={(e) => {
                          const next = [...formHazards]
                          next[hi] = { ...next[hi], severity: e.target.value as any }
                          setFormHazards(next)
                        }}
                      >
                        {SEVERITY_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {niceEnum(opt)}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className='col-md-4 mb-2'>
                      <Form.Label className={FORM_LABEL}>Risk factor</Form.Label>
                      <Form.Select
                        value={h.riskFactor}
                        onChange={(e) => {
                          const next = [...formHazards]
                          next[hi] = { ...next[hi], riskFactor: e.target.value as any }
                          setFormHazards(next)
                        }}
                      >
                        {RISK_FACTOR_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {niceEnum(opt)}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                  <Form.Label className={FORM_LABEL}>Existing control measures</Form.Label>
                  {h.existingControlLines.map((line, li) => (
                    <div key={li} className='d-flex gap-1 mb-1'>
                      <Form.Control
                        value={line}
                        onChange={(e) => {
                          const next = [...formHazards]
                          const lines = [...next[hi].existingControlLines]
                          lines[li] = e.target.value
                          next[hi] = { ...next[hi], existingControlLines: lines }
                          setFormHazards(next)
                        }}
                      />
                      <Button
                        variant='outline-danger'
                        size='sm'
                        onClick={() => {
                          const next = [...formHazards]
                          next[hi].existingControlLines = next[hi].existingControlLines.filter(
                            (_, i) => i !== li
                          )
                          setFormHazards(next)
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant='light'
                    size='sm'
                    className='mb-3 d-block'
                    onClick={() => {
                      const next = [...formHazards]
                      next[hi].existingControlLines = [...next[hi].existingControlLines, '']
                      setFormHazards(next)
                    }}
                  >
                    + Add existing control measure
                  </Button>
                  <Form.Label className={`${FORM_LABEL} d-block`}>Further control measures</Form.Label>
                  {h.furtherControlLines.map((line, li) => (
                    <div key={li} className='d-flex gap-1 mb-1'>
                      <Form.Control
                        value={line}
                        onChange={(e) => {
                          const next = [...formHazards]
                          const lines = [...next[hi].furtherControlLines]
                          lines[li] = e.target.value
                          next[hi] = { ...next[hi], furtherControlLines: lines }
                          setFormHazards(next)
                        }}
                      />
                      <Button
                        variant='outline-danger'
                        size='sm'
                        onClick={() => {
                          const next = [...formHazards]
                          next[hi].furtherControlLines = next[hi].furtherControlLines.filter(
                            (_, i) => i !== li
                          )
                          setFormHazards(next)
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant='light'
                    size='sm'
                    className='d-block'
                    onClick={() => {
                      const next = [...formHazards]
                      next[hi].furtherControlLines = [...next[hi].furtherControlLines, '']
                      setFormHazards(next)
                    }}
                  >
                    + Add further control measure
                  </Button>
                </div>
              ))}
              <Button variant='light-primary' size='sm' onClick={() => setFormHazards([...formHazards, emptyHazard()])}>
                + Add hazard
              </Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={stickyModalFooterStyle}>
          <Button variant='secondary' onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button variant='primary' onClick={submitTaskForm} disabled={saving}>
            {saving ? 'Saving…' : 'Save task'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position='top-center'
        autoClose={4500}
        limit={4}
        newestOnTop
        pauseOnFocusLoss={false}
        closeOnClick
        draggable={false}
        theme='colored'
        className='risk-assessment-library-toasts'
        toastClassName='shadow-sm'
        bodyClassName='fw-medium'
      />
    </div>
  )
}

export default RiskAssessmentLibraryPage
